import type { ReactNode } from 'react';
import { Navigate } from 'react-router';

import { pathTo } from '../routes/routes';
import { useSession } from './sessionStore';

export interface RequireSessionProps {
  readonly children: ReactNode;
}

/**
 * A route only a signed-in person reaches.
 *
 * The three states are three different answers, and collapsing any two of them is the bug this component
 * exists to avoid:
 *
 * - **`restoring`** — a reload has an `HttpOnly` refresh cookie the page cannot read, so whether there is
 *   a session is a question that takes one request to answer. Nothing is rendered while it is in flight.
 *   Treating this as signed-out would bounce every reload to the welcome screen and back.
 * - **`signed-out`** — to `ONB-01`, replacing the entry so that Back does not come straight back to a
 *   screen that will bounce them again.
 * - **`signed-in`** — the screen.
 *
 * Nothing is rendered during `restoring` rather than a spinner: it is one request on a warm connection,
 * and a spinner that appears and vanishes reads as a flicker rather than as progress.
 */
export function RequireSession({ children }: RequireSessionProps): React.JSX.Element | null {
  const { status } = useSession();

  if (status === 'restoring') {
    return null;
  }

  if (status === 'signed-out') {
    return <Navigate to={pathTo('welcome')} replace />;
  }

  return <>{children}</>;
}
